import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { translate, TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isNil } from 'lodash';

import { CallStatsOverTime, EntityStats, EntityStatsDisplayWindow } from '@pwp-common';

@UntilDestroy()
@Component({
  selector: 'app-calls-over-time',
  templateUrl: './calls-over-time.component.html',
  styleUrls: ['./calls-over-time.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallsOverTimeComponent implements OnInit, OnChanges {
  /////////////////////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////////////////////

  @Input() public orgStats: EntityStats;

  @Input() public window: EntityStatsDisplayWindow;

  public traces: any = [];

  public layout: any = {};

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  /////////////////////////////////////////////////////////////////////////////////////////////

  constructor(
    private translocoService: TranslocoService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      this.refreshData();
      this.changeDetectorRef.detectChanges();
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('orgStats' in changes || 'window' in changes) {
      this.refreshData();
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Data
  /////////////////////////////////////////////////////////////////////////////////////////////

  private refreshData() {
    this.resetData();
    if (isNil(this.orgStats) || isNil(this.window)) {
      return;
    }

    const callStatsOverTime = new CallStatsOverTime(this.orgStats, this.window);

    // Requested Service
    const iNumServiceRequested = callStatsOverTime.getLabeledData(callStatsOverTime.iNumServiceRequested);
    this.traces.push({
      x: iNumServiceRequested.getLabels(),
      y: iNumServiceRequested.getData(),
      type: 'bar',
      name: translate('calls-over-time.requestedService'),
      text: iNumServiceRequested.getData(),
      textposition: 'auto',
    });

    // Number Answered
    const iNumAnswered = callStatsOverTime.getLabeledData(callStatsOverTime.iNumAnswered);
    this.traces.push({
      x: iNumAnswered.getLabels(),
      y: iNumAnswered.getData(),
      type: 'line',
      name: translate('calls-over-time.answered'),
    });

    // Missed
    const iNumUnexpectedlyMissed = callStatsOverTime.getLabeledData(callStatsOverTime.iNumUnexpectedlyMissed);
    this.traces.push({
      x: iNumUnexpectedlyMissed.getLabels(),
      y: iNumUnexpectedlyMissed.getData(),
      type: 'line',
      name: translate('calls-over-time.missed'),
    });
  }

  private refreshLayout() {
    this.layout = {
      title: translate('calls-over-time.title'),
      showlegend: true,
      hovermode: 'x unified',
      hoverlabel: { namelength: -1 },
      legend: { orientation: 'h' },
      xaxis: { title: translate('calls-over-time.monthTitle') },
      yaxis: { title: translate('calls-over-time.callsTitle') },
    };
  }

  private resetData() {
    this.traces = [];
    this.refreshLayout();
  }
}
